import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Phone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyListings = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyListings();
  }, []);

  const fetchMyListings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/listings/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(response.data);
    } catch (error) {
      toast.error('Failed to fetch your listings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (listingId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Listing deleted successfully');
      fetchMyListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete listing');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="my-listings-page">
        <div className="text-lg">Loading your listings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="my-listings-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
            My Listings
          </h1>
          <p className="text-neutral-600">Manage your chicken listings</p>
        </div>

        {listings.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-neutral-600">You haven't created any listings yet</p>
          </div>
        ) : (
          <div className="marketplace-grid">
            {listings.map((listing) => (
              <Card 
                key={listing.id} 
                className="listing-card overflow-hidden border-neutral-200 hover:shadow-lg"
                data-testid="my-listing-card"
              >
                <div className="image-preview-container">
                  <img 
                    src={listing.image_url} 
                    alt={listing.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <CardHeader>
                  <CardTitle className="text-xl" style={{ fontFamily: 'Young Serif, serif' }}>
                    {listing.title}
                  </CardTitle>
                  <CardDescription className="text-base font-medium text-secondary">
                    {listing.breed}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <p className="text-neutral-700 line-clamp-2">{listing.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-neutral-600">
                      <MapPin className="w-4 h-4 mr-2 text-primary" />
                      {listing.location}
                    </div>
                    <div className="flex items-center text-sm text-neutral-600">
                      <Phone className="w-4 h-4 mr-2 text-primary" />
                      {listing.contact}
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="text-3xl font-bold text-primary mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
                      ₹{listing.cost.toFixed(2)}
                    </div>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="destructive" 
                          className="w-full rounded-full"
                          data-testid="delete-listing-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Listing
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete your listing.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(listing.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyListings;